import {Injectable} from "@angular/core";
import {Actions, createEffect, ofType} from "@ngrx/effects";
import {Store} from "@ngrx/store";
import {geoActions, providersActions} from "../actions"
import {catchError, exhaustMap, map, mergeMap, of, withLatestFrom} from "rxjs";
import * as ProviderActions from '@core/ngrx/actions/provider.actions';
import {ProviderService} from '@core/service/provider.service';
import {Provider} from '@core/model/providers';
import {PaginatedResultModel} from '@core/model/paginated-result.model';
import {GlobalProvider} from '@core/model/global-provider';

@Injectable()
export class ProviderEffects {
  constructor(
    private actions$: Actions,
    private readonly store: Store,
    private providersService: ProviderService
  ) {
  }

  loadProviders = createEffect(() =>
    this.actions$.pipe(
      ofType(providersActions.loadProviders),
      withLatestFrom(this.store),
      exhaustMap(([action, store]) => this.providersService.getProviders(action.mapId).pipe(
        map(response => {
          const providers = (response as any).data.providers.map((provider: any) => {
            return {
              id: provider.id,
              name: provider.providerName,
              base64Icon: provider.imageBase64,
              color: provider.color,
              isVisible: true
            } as Provider;
          });
          return providersActions.providersLoaded({providers: providers})
        }),
        catchError((error: any) => {
          return of(geoActions.queryGeoFailure({msg: "error"}));
        })
      ))
    ));

  loadGlobalProviders$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProviderActions.loadGlobalProviders),
      mergeMap(({params}) =>
        this.providersService.getGlobalProviders(params).pipe(
          map((response) => {
            const result: PaginatedResultModel<Provider> = {
              ...response,
              data: response.data.map((provider: GlobalProvider) => {
                return {
                  id: provider.id,
                  name: provider.providerName,
                  description: provider.description,
                  base64Icon: provider.logo,
                  extension: provider.extension,
                  isVisible: true
                } as Provider;
              }) as Provider[],
            };
            return ProviderActions.loadGlobalProvidersSuccess({response: result})
          }),
          catchError(() => of(ProviderActions.loadGlobalProvidersFailure()))
        )
      )
    )
  );

  deleteGlobalProvider$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProviderActions.deleteGlobalProvider),
      mergeMap(({id}) =>
        this.providersService.deleteGlobalProvider(id).pipe(
          map(() => ProviderActions.deleteGlobalProviderSuccess()),
          catchError((error) =>
            of(ProviderActions.deleteGlobalProviderFailure({error}))
          )
        )
      )
    )
  );

  getGlobalProvider$ = createEffect(() =>
    this.actions$.pipe(
      ofType(providersActions.getGlobalProvider),
      withLatestFrom(this.store),
      exhaustMap(([action, store]) => this.providersService.getGlobalProvider(action.id).pipe(
        map(response => {
          const provider = (response as any);
          const providerModel = {
            id: provider.id,
            name: provider.providerName,
            base64Icon: provider.logo,
            extension:provider.extension,
            description: provider.description,
          } as Provider;
          return providersActions.getGlobalProviderSuccess({provider: providerModel})
        }),
        catchError((error: any) => {
          return of(providersActions.getGlobalProviderFailure({error: "error"}));
        })
      ))
    ));

  saveGlobalProvider$ = createEffect(() =>
    this.actions$.pipe(
      ofType(providersActions.saveGlobalProvider),
      withLatestFrom(this.store),
      exhaustMap(([action, store]) => this.providersService.saveGlobalProvider(action.provider).pipe(
        map(response => {
          const provider = (response as any);
          const providerModel = {
            id: provider.id,
            name: provider.providerName,
            base64Icon: provider.logo,
            extension:provider.extension
          } as Provider;
          return providersActions.saveGlobalProviderSuccess({provider: providerModel})
        }),
        catchError((error: any) => {
          return of(providersActions.saveGlobalProviderFailure({error: error.error}));
        })
      ))
    ));

}
